import { toClinicDate, clinicNow } from './clinicTime';

/**
 * Whole years and months between a date of birth and today, counted by the calendar.
 *
 * The one implementation, because there were four and two of them were wrong. Dividing elapsed
 * milliseconds by an average year of 365.25 days is not how old anyone is: from 2008-09-20 to that
 * person's 18th birthday is 6,574 days, and 6,574 / 365.25 is 17.998. So a patient read as 17 on
 * the day they turned 18, and whether it happened at all depended on how many leap days fell in
 * that particular span. In the vitals engine that meant being judged against adolescent ranges on
 * an adult's birthday, which is a clinical difference rather than a cosmetic one.
 *
 * Clinic wall-clock on both sides. A date-only DOB parsed with new Date() is UTC midnight, which
 * reads a day early west of UTC and can shift a baby's age by a whole month.
 *
 * @returns {{years: number, months: number}|null} null when there is no usable date of birth
 */
export const ageParts = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const today = clinicNow();
  const birth = toClinicDate(dateOfBirth);
  if (!birth || isNaN(birth.getTime())) return null;
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  if (today.getDate() < birth.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  return { years, months };
};

/** Whole years old today, or null. */
export const ageInYears = (dateOfBirth) => {
  const parts = ageParts(dateOfBirth);
  return parts ? parts.years : null;
};

/**
 * Human-readable age from a date of birth, tuned for a paediatric-heavy patient list where
 * "0 years" would be useless.
 *
 *   > 2 years   → "34 years"
 *   0-2 years   → "1 year 3 months"
 *   under 1     → "5 months"
 *   under 1 mo  → "newborn"
 *
 * Shared by the patient list, the patient detail view and the printed token receipt so all three
 * describe the same patient identically.
 */
export const formatAge = (dateOfBirth) => {
  if (!dateOfBirth) return '0';
  // Clinic wall-clock on both sides. A date-only DOB parsed with new Date() is UTC midnight,
  // which reads a day early west of UTC and can shift a baby's age by a whole month.
  const parts = ageParts(dateOfBirth);
  if (!parts) return '0';
  const { years, months } = parts;
  if (years > 2) return `${years} years`;
  if (years === 0 && months === 0) return 'newborn';
  if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`;
  return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
};

/**
 * A patient's age for the screen, honest about an estimate: "about 6 years" when the birth date
 * was derived from an age the family gave, plain "6 years" when it is a fact. Works for anything
 * carrying dateOfBirth and dobEstimated (a patient, a look-alike card, a report row).
 */
export const formatPatientAge = (patient) => {
  if (!patient || !patient.dateOfBirth) return null;
  if (!patient.dobEstimated) return formatAge(patient.dateOfBirth);
  // An estimate is only ever as fine as the family's answer: whole years, or months for a baby.
  const parts = ageParts(patient.dateOfBirth);
  if (!parts) return null;
  const { years, months } = parts;
  if (years >= 1) return `about ${years} year${years !== 1 ? 's' : ''}`;
  if (months >= 1) return `about ${months} month${months !== 1 ? 's' : ''}`;
  return 'newborn';
};

const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Whole months back without running over into the next month: 31 October less 30 months is
// 30 April, where setMonth alone gives "31 April", which is 1 May.
const monthsBack = (d, n) => {
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
};

/**
 * The birth date to store when the family knows an age but not a date: the MIDDLE of that span
 * of life, so it stays right for as long as possible either way. "6 years" becomes today minus
 * 6 years 6 months (the child is somewhere between 6.0 and 7.0); "8 months" becomes today minus
 * 8 months 15 days. Returns "YYYY-MM-DD" on the clinic's calendar, or null for nothing usable.
 */
export const dobFromAge = ({ years, months } = {}) => {
  const y = Number.parseInt(years, 10);
  const m = Number.parseInt(months, 10);
  const hasYears = Number.isFinite(y) && y >= 0;
  const hasMonths = Number.isFinite(m) && m >= 0;
  if (!hasYears && !hasMonths) return null;
  const totalMonths = (hasYears ? y : 0) * 12 + (hasMonths ? m : 0);
  if (totalMonths <= 0 && !(hasMonths && m === 0)) return null;
  const d = clinicNow();
  d.setHours(12, 0, 0, 0);
  if (hasMonths && m > 0) {
    monthsBack(d, totalMonths);
    d.setDate(d.getDate() - 15);
  } else if (hasMonths && m === 0 && !hasYears) {
    d.setDate(d.getDate() - 15);
  } else {
    monthsBack(d, totalMonths + 6);
  }
  return isoDate(d);
};
